

import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { MutationStatus } from 'react-query';
import { ApiButton, Box, Button, ErrorInfo } from 'web-library';
import { ResitCoordinatorAccess } from '../../../../models/@candidate/candidateResitRegistrationSchema';
import RouteKey from '../../../../routes/key';
import { trueToRender } from '../../../../utils/ComponentUtils';
import { booleanToString } from '../../../../utils/StringUtils';
import PaymentButton, { PaymentMethod } from '../../../payment/components/PaymentButton';
import { useGroupFeeApis, useResitAccess } from '../../logic/useCoordinatorResit';
import { CoordinatorResitAction } from '../../logic/useCoordinatorResitData';
import { GroupResitSchema } from '../../../../api-refactored/group-resit-registration/model';
import { ResitTransactionResponse } from '../../../../api-refactored/group-resit-registration/usePostConfirmAndPay';
import { ApplicationType } from '../../../../api/candidate/config/model';
import { useGetPaymentAccess } from '../../../../api-refactored/financial/useGetPaymentAccess';

const useStyles = makeStyles()((theme) =>
({
  stepNavigationActionBarContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    backgroundColor: theme.color.background.bgAlt,
    width: '100%',
  },
  buttonsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    backgroundColor: theme.color.background.bgAlt,
  },
  rightButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  button: {
    marginRight: theme.spacing(2),
  },
}));

const { resitRegistration } = RouteKey.child;
const { coordinatorResitTransaction, coordinatorSendEmailSuccess } = RouteKey[
  'resit-registration'
].child;

const showAllowPayment = (step: number, accessData: ResitCoordinatorAccess | undefined) =>
  step === 3 && accessData?.payOnbehalf;

const showAllowEmail = (step: number, accessData: ResitCoordinatorAccess | undefined) =>
  step === 3 && accessData?.sendBulkEmail;

interface CoordinatorResitActionBarProps {
  candidateID: readonly string[];
  candidatesDetails:
  | {
    readonly [x: string]: any;
  }
  | undefined;
  dispatch: React.Dispatch<CoordinatorResitAction>;

  step: number;
  stepNavigate: (forward: boolean) => void;
  setDeclareError: React.Dispatch<React.SetStateAction<boolean>>;
  groupFee: GroupResitSchema | null;
  isAgreed: boolean;
  continueStatus: MutationStatus;
  resitUuid: string;
}

const addAccessLink = (resitUuid: string, groupFee: GroupResitSchema, method = '') => ({
  uuid: resitUuid,
  groupResitPayment: {
    resitCandidateList: groupFee.resitCandidateList,
    paymentMode: method,
  },
});

const CoordinatorResitActionBar = ({
  candidateID,
  candidatesDetails,
  dispatch,
  step,
  stepNavigate,
  setDeclareError,
  groupFee,
  isAgreed,
  continueStatus,
  resitUuid,
}: CoordinatorResitActionBarProps) => {
  const { classes } = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  const [emailButtonStatus, setEmailButtonStatus] = useState<MutationStatus>('idle');

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);

  useGetPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.bankTransferMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  const {
    sendGroupFeeError,
    sendEmailError,
    setSendEmailError,
    postConfirmAndPayMutation,
  } = useGroupFeeApis();

  const { accessData } = useResitAccess();

  const allHaveSelectedSubject = useMemo(() => {
    if (candidateID.length === 0) {
      return false;
    }
    const validState = candidateID.map(
      (nric) => (candidatesDetails?.[nric]?.selectedSubjects?.length || 0) > 0
    );
    return validState.reduce((sum, next) => sum && next, true);
  }, [candidateID, candidatesDetails]);

  const onSuccessGroupFeeSaving = useCallback(
    (res: ResitTransactionResponse) => {
      dispatch({
        type: 'UPDATE_INTERNAL_TRANSACTION_REF',
        payload: res.internalTransactionRef,
      });
    },
    [dispatch]
  );

  /** ALEX TEST to handle user click payment button
   * */
  const handleClickPaymentButtonTest = (callback: () => void) => {
    setDeclareError(!isAgreed);
    if (isAgreed && groupFee) {
      callback();
    }
  };

  /** ALEX TEST to handle payment option
   * */
  const handlePaymentOptionTest = (
    callback: (internalTransactionRef: string) => void,
    method: string
  ) => {
    if (isAgreed && groupFee) {
      postConfirmAndPayMutation.mutate(addAccessLink(resitUuid, groupFee, method), {
        onSuccess: (resitTransaction) => {
          onSuccessGroupFeeSaving(resitTransaction);
          if (resitTransaction.paymentSuccess) {
            history.push(
              `/${resitRegistration}/${coordinatorResitTransaction.route}?internalTransactionRef=${resitTransaction.internalTransactionRef}`
            );
          }

          callback(resitTransaction.internalTransactionRef);
        },
      });
    }
  };

  return (
    <Box className={classes.stepNavigationActionBarContainer}>
      <Box className={classes.buttonsContainer}>
        <Box className={classes.rightButtonContainer}>
          <Button
            id="button-resitAction-previous"
            className={classes.button}
            variant="outlined"
            onClick={() => {
              if (step === 3) {
                setDeclareError(false);
              }
              stepNavigate(false);
            }}
          >
            {booleanToString(step !== 0, 'Previous', 'Cancel')}
          </Button>

          {/** Render payment if allow payment & fee is not zero */}
          {trueToRender(
            showAllowPayment(step, accessData) && groupFee?.totalPayment.totalFee !== '0.00',
            <Box className={classes.button}>
              <PaymentButton
                // disabled={!isAgreed}
                isUniqueLink={false}
                // buttonClassName={classes.button}
                paymentMethodsAvailable={paymentAccessDetailsOption}
                onClickPaymentButton={(callback) => {
                  handleClickPaymentButtonTest(callback);
                }}
                onHandlePaymentMethod={(callback, method) => {
                  handlePaymentOptionTest(callback, method);
                }}
                onSuccessPayment={(txnId) => {
                  history.push(
                    `/${resitRegistration}/${coordinatorResitTransaction.route}?internalTransactionRef=${txnId}`
                  );
                }}
                type={ApplicationType.RESIT}
                buttonErrorText={t('components->>PaymentExceptionError.ResitMessage')}
              />
            </Box>
          )}

          {/** Render direct registration if allow payment & fee is zero */}
          {trueToRender(
            showAllowPayment(step, accessData) && groupFee?.totalPayment.totalFee === '0.00',
            <ApiButton
              id="button-resitAction-confirmRegistration"
              status={postConfirmAndPayMutation.status}
              onClick={() => {
                setDeclareError(!isAgreed);
                if (isAgreed && groupFee) {
                  postConfirmAndPayMutation.mutate(addAccessLink(resitUuid, groupFee), {
                    onSuccess: (resitTransaction) => {
                      onSuccessGroupFeeSaving(resitTransaction);
                      history.push(
                        `/${resitRegistration}/${coordinatorResitTransaction.route}?internalTransactionRef=${resitTransaction.internalTransactionRef}`
                      );
                    },
                  });
                }
              }}
            >
              {t('resit->>RegisterResit.Confirm Registration')}
            </ApiButton>
          )}

          {trueToRender(
            showAllowEmail(step, accessData) && groupFee?.totalPayment.totalExamFee !== '0.00',
            <ApiButton
              id="button-resitAction-sendEmail"
              status={emailButtonStatus}
              className={classes.button}
              onClick={() => {
                setEmailButtonStatus('loading');
                setDeclareError(!isAgreed);
                if (isAgreed && groupFee) {
                  setSendEmailError('');
                  postConfirmAndPayMutation.mutate(
                    addAccessLink(resitUuid, groupFee, 'PAYMENT_LINK'),
                    {
                      onSuccess: () => {
                        history.push(`/${resitRegistration}/${coordinatorSendEmailSuccess.route}`);
                        setEmailButtonStatus('success');
                      },
                      onError: () => setEmailButtonStatus('error'),
                    }
                  );
                }
              }}
            >
              Send Email to Candidates
            </ApiButton>
          )}

          {step !== 3 && (
            <ApiButton
              id="button-resitAction-continue"
              disabled={!allHaveSelectedSubject}
              className={classes.button}
              onClick={() => stepNavigate(true)}
              status={continueStatus}
            >
              Continue
            </ApiButton>
          )}
        </Box>
      </Box>

      {sendGroupFeeError !== '' && <ErrorInfo label={sendGroupFeeError} />}

      {sendEmailError !== '' && <ErrorInfo label={sendEmailError} />}
    </Box>
  );
};

export default CoordinatorResitActionBar;
